import { ReactNode } from 'react';

import { ErrorOutline } from '@mui/icons-material';
import {
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  SxProps,
  Theme,
  Typography,
} from '@mui/material';

import { useLanguage, useWindowSize } from '../../hooks';

interface StyleProps {
  severity: 'error' | 'warning' | 'info' | 'success';
}

const getButtonStyle = (color: 'error' | 'secondary'): SxProps<Theme> => {
  switch (color) {
    case 'error':
      return {
        borderColor: (theme: Theme) => theme.palette.error.main,
        backgroundColor: (theme: Theme) => theme.palette.error.main,
        color: (theme: Theme) => theme.palette.error.contrastText,
        '&.MuiButton-root': {
          '& :focus': {
            borderColor: (theme: Theme) => theme.palette.error.dark,
            backgroundColor: (theme: Theme) => theme.palette.error.dark,
          },
          '&:hover': {
            borderColor: (theme: Theme) => theme.palette.error.dark,
            backgroundColor: (theme: Theme) => theme.palette.error.dark,
          },
        },
      };
    default:
      return {
        borderColor: (theme: Theme) => theme.palette.secondary.light,
        color: (theme: Theme) => theme.palette.text.secondary,
        '&.MuiButton-root': {
          '& :focus': {
            borderColor: (theme: Theme) => theme.palette.secondary.dark,
          },
          '&:hover': {
            borderColor: (theme: Theme) => theme.palette.secondary.dark,
          },
        },
      };
  }
};

const customStylesHandler = ({ severity }: StyleProps) => {
  return {
    root: {
      '& .MuiDialog-paper': {
        width: '473px',
        minHeight: '190px',
        height: 'auto',
        boxSizing: 'border-box',
        borderRadius: '20px',
        padding: '32px 24px 24px 24px',
        boxShadow: '2px 4px 16px rgba(15, 59, 96, 0.18)',
      },
      '& .MuiDialog-paperFullScreen': {
        height: '100%',
        borderRadius: '0px',
      },
    },
    header: {
      alignItems: 'flex-start',
      display: 'flex',
      gap: '18px',
      padding: '0px',
      fontSize: '16px',
      lineHeight: '24px',
    },
    warningIcon: {
      color: (theme: Theme) => (severity === 'error' ? theme.palette.error.main : '#F5D409'),
    },
    confirm: {
      fontWeight: 500,
      lineHeight: '24px',
    },
    folderName: {
      fontWeight: 500,
      lineHeight: '24px',
    },
    content: {
      display: 'flex',
      padding: '0px 0px 40px 40px',
      justifyContent: 'flex-start',
      marginTop: '8px',
      lineHeight: '24px',
      whiteSpace: 'pre-line',
    },
    actions: {
      padding: '0px',
    },
    cancelButton: {
      borderColor: (theme: Theme) => theme.palette.secondary.light,
      color: (theme: Theme) => theme.palette.text.secondary,
      '&.MuiButton-root': {
        '& :focus': {
          borderColor: (theme: Theme) => theme.palette.secondary.dark,
        },
        '&:hover': {
          borderColor: (theme: Theme) => theme.palette.secondary.dark,
        },
      },
    },
  } as Record<string, SxProps<Theme>>;
};

type ConfirmActionDialogProps = {
  warningTitle?: ReactNode;
  warningMessage?: ReactNode;
  isOpen: boolean;
  onClose: () => void;
  onConfirmAction: () => void;
  cancelActionButtonText?: string;
  confirmActionButtonText?: string;
  severity?: 'error' | 'warning' | 'info' | 'success';
  loading?: boolean;
  /**
   * Keeps Cancel clickable while `loading`. Off by default, because for most
   * callers `loading` means the confirmed action is already running. Turn it on
   * when `loading` is a pre-check the dialog runs before the person decides: the
   * dialog must not become inescapable if that request never answers.
   */
  allowCancelWhileLoading?: boolean;
  cancelActionButtonColor?: 'error' | 'secondary';
  mandatoryButtons?: boolean;
  confirmButtonColor?: 'primary' | 'error';
  confirmButtonTestId?: string;
  cancelButtonTestId?: string;
};

export const ConfirmActionDialog = ({
  warningTitle,
  warningMessage,
  isOpen,
  onClose: handleClose,
  onConfirmAction: handleConfirmAction,
  cancelActionButtonText,
  confirmActionButtonText,
  severity = 'warning',
  loading = false,
  allowCancelWhileLoading = false,
  cancelActionButtonColor = 'secondary',
  mandatoryButtons = false,
  confirmButtonColor = 'primary',
  confirmButtonTestId,
  cancelButtonTestId,
}: ConfirmActionDialogProps) => {
  const customStyles = customStylesHandler({ severity });
  const lang = useLanguage();
  const { width } = useWindowSize();

  const wrappedHandleClose = (_event: React.MouseEvent<HTMLButtonElement>, reason: string) => {
    if (mandatoryButtons && (reason === 'escapeKeyDown' || reason === 'backdropClick')) {
      console.log('Disabled actions: Escape key or backdrop click');
      return;
    }
    handleClose();
  };

  return (
    <Dialog
      sx={customStyles.root}
      open={isOpen}
      onClose={wrappedHandleClose}
      fullScreen={width < 480}
    >
      <DialogTitle sx={customStyles.header}>
        <ErrorOutline sx={customStyles.warningIcon} />
        <Typography
          sx={customStyles.confirm}
          variant='h3'
        >
          {warningTitle}
        </Typography>
      </DialogTitle>

      <DialogContent sx={customStyles.content}>
        <Typography variant='h5'>{warningMessage}</Typography>
      </DialogContent>

      <DialogActions sx={customStyles.actions}>
        <Button
          onClick={handleClose}
          sx={getButtonStyle(cancelActionButtonColor)}
          variant='outlined'
          disabled={loading && !allowCancelWhileLoading}
          data-testid={cancelButtonTestId}
        >
          {cancelActionButtonText ?? lang.uiComponents.cancel}
        </Button>
        <Button
          onClick={handleConfirmAction}
          variant='contained'
          color={confirmButtonColor}
          disabled={loading}
          data-testid={confirmButtonTestId}
          startIcon={
            loading && (
              <CircularProgress
                size={14}
                color='inherit'
              />
            )
          }
        >
          {confirmActionButtonText ?? lang.uiComponents.confirm}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
